import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Box, Checkbox, Flex, HStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../../../constants/cypressIds';
import dataTypes from '../../../../../../../../../../../../constants/dataTypes';
import { fieldInputTypes } from '../../../../../../../../../../../../constants/fieldInputTypes';
import colors from '../../../../../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../../../../../types/RequirementTemplateBlueprintType';
import { createMap, get } from '../../../../../../../../../../../../utils/arrayUtil';
import FormInput from '../../../../../../../../../../../form/FormInput';
import InputLabel from '../../../../../../../../../../../form/InputLabel';
import LoadingSpinner from '../../../../../../../../../../../Spinner';

const PolicyFieldValuesForm = ({ formId, onSubmit, blueprint }) => {
  const policyType = useSelector(blueprint.selectCurrentPolicyType);
  const allPolicyProperties = policyType?.properties;
  const policyBlockData = useSelector(blueprint.selectCurrentRequirementTemplateBlock);
  const blockProperties = policyBlockData ? get(policyBlockData, '0.requirementTemplateBlockProperties') : undefined;
  const blockPropertiesMap = blockProperties?.length ? createMap(blockProperties, 'requirementId') : {};

  const getDefaultValues = () => {
    const monetaryProperties = (blockProperties || []).filter(property => property?.requirement?.dataType === dataTypes.Monetary);
    const alphanumericProperties = (blockProperties || []).filter(property => property?.requirement?.dataType === dataTypes.String) || [];

    let defaultLimitValues = {};
    monetaryProperties.forEach(property => {
      defaultLimitValues = Object.assign(
        defaultLimitValues,
        { [`${fieldInputTypes.Monetary}.${property.requirementId}`]: property.value }
      );
    });

    alphanumericProperties.forEach(property => {
      defaultLimitValues = Object.assign(
        defaultLimitValues,
        { [`${fieldInputTypes.AlphaNumeric}.${property.requirementId}`]: property.value }
      );
    });

    return defaultLimitValues;
  };

  const useFormMethods = useForm({ defaultValues: getDefaultValues() });

  const { handleSubmit, register, setValue } = useFormMethods;
  useEffect(() => {
    if (policyType) {
      setValue('policyName', policyType.displayName);
    }
  }, [policyBlockData, setValue]);

  if (policyBlockData) {
    return (
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Box pb={2}>
          <Box maxH="450px">
            <InputLabel labelName="Required Coverage" mb={4} />
          </Box>
          {
              allPolicyProperties
                ?.filter(property => property.dataType === dataTypes.Boolean).map(property => {
                  const blockProperty = blockPropertiesMap[property?.id];
                  return (
                    <HStack
                      alignItems="center"
                      flexFlow="row noWrap"
                      height="40px"
                      key={`${property.dataType}.${property.id}`}
                      position="relative"
                    >
                      <Checkbox
                        color={colors.primaryScale['600']}
                        data-cy={CypressIds.policyTypePropertyRequirementForm(policyType.id, property.id)}
                        defaultChecked={blockProperty?.value && Number(blockProperty?.value) > 0}
                        key={`${property.dataType}.${property.id}`}
                        {...register(`${property.dataType}.${property.id}`)}
                      />
                      <Box
                        color={colors.greyScale['800']}
                        overflowWrap="anywhere"
                        textStyle="bodyMd"
                      >
                        {property.displayName}
                      </Box>
                    </HStack>
                  );
                })
          }
          <Box mt={8}>
            {
            allPolicyProperties
              ?.filter(property => [dataTypes.String, dataTypes.Monetary]
                .includes(property.dataType)).map(property => {
                const type = property.dataType === dataTypes.String
                  ? fieldInputTypes.AlphaNumeric : property.dataType;

                return (
                  <HStack
                    alignItems="center"
                    flexFlow="row noWrap"
                    height="40px"
                    key={`${type}.${property.id}`}
                    mt={4}
                    position="relative"
                  >
                    <Box
                      color={colors.greyScale['500']}
                      overflowWrap="anywhere"
                      pb={4}
                      pr={2}
                      textStyle="bodySm500"
                      w="100%"
                    >
                      {property.displayName}
                    </Box>
                    <FormInput
                      cypressId={CypressIds.policyTypePropertyRequirementForm(policyType.id, property.id)}
                      dataType={property.dataType}
                      inputName={`${type}.${property.id}`}
                      useFormMethods={useFormMethods}
                      validationRules={{
                        maxLength: {
                          value: 32,
                          message: 'Policy Limit must have fewer than 32 characters'
                        }
                      }}
                    />
                  </HStack>
                );
              })
           }
          </Box>
        </Box>
      </form>
    );
  }

  return (
    <Flex h="100%" justifyContent="center">
      <LoadingSpinner />
    </Flex>
  );
};

PolicyFieldValuesForm.propTypes = {
  formId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  blueprint: RequirementTemplateBlueprintType
};

export default PolicyFieldValuesForm;
